// ============================================================================
// ClassAssignForm (F-753) — LE lieu de saisie off_grid / role_fit (NT-L09-02/03)
// ============================================================================
// Affectation classe / niveau / echelon / date + drapeaux « hors grille » (Notion
// A) et « adequation au role » (Notion B). C'est LA surface ou off_grid_reason_private
// (requis si off_grid, M-752) et role_fit_note se posent — vue GESTION uniquement.
//
// Matrice §G : ces champs prives arrivent du DTO gestion et repartent par
// useSetCompensation. Ils ne franchissent JAMAIS une surface self (le serveur
// re-verifie, le client ne fait que refleter).
//
// [!] P-ADM-16 (AD-D) : setCompensationRecord = UPDATE PLEIN. Le formulaire
// s'hydrate depuis getManagementCompensationView (record) AVANT tout PUT —
// off_grid_reason_private / off_grid_reason_public / role_fit_note sont
// pre-remplis et renvoyes tels quels s'ils ne sont pas touches. Payload via
// buildCompensationPayload (garde unitaire : editer l'echelon seul n'efface
// pas la raison privee).
//
// M-752 : decocher off_grid -> l'UI efface les 2 raisons + desactive l'echelon.
//         decocher role_fit -> efface la note. (Invariant AUSSI serveur.)
// M-754 : echelon plafonne a maxEchelonForNiveau ; changement de classe -> reset niveau.

import { useState } from 'react';
import { GS_COMPONENTS } from '@gs/ui';
import { Save } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useSetCompensation, managementErrorCode, type GridClass, type ManagementCompensationRecord } from '../../hooks/useManagement';
import { buildCompensationPayload } from '../../lib/admin/compensation-payload';

export function ClassAssignForm({
  email,
  record,
  classes,
  onError,
}: {
  email: string;
  record: ManagementCompensationRecord | null;
  classes: GridClass[];
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const setComp = useSetCompensation();

  const [classId, setClassId] = useState(record?.classId ?? '');
  const [niveau, setNiveau] = useState(record?.currentNiveau != null ? String(record.currentNiveau) : '');
  const [echelon, setEchelon] = useState(String(record?.currentEchelon ?? 1));
  const [hireDate, setHireDate] = useState(record?.hireDate?.slice(0, 10) ?? '');
  const [offGrid, setOffGrid] = useState(record?.offGrid ?? false);
  const [reasonPrivate, setReasonPrivate] = useState(record?.offGridReasonPrivate ?? '');
  const [reasonPublic, setReasonPublic] = useState(record?.offGridReasonPublic ?? '');
  const [roleFit, setRoleFit] = useState(record?.roleFitFlag ?? false);
  const [roleFitNote, setRoleFitNote] = useState(record?.roleFitNote ?? '');
  const [stepError, setStepError] = useState<string | null>(null);

  const cls = classes.find((c) => c.id === classId) ?? null;
  const selectedNiveau = cls?.niveaux.find((n) => String(n.level) === niveau) ?? null;
  const maxEchelon = selectedNiveau?.echelonMax ?? cls?.echelons.length ?? 60;

  function submit() {
    if (!classId) {
      setStepError(t('gest:management.errors.invalid'));
      return;
    }
    // M-752 : hors grille sans raison privee = refus (aussi verifie serveur).
    if (offGrid && !reasonPrivate.trim()) {
      setStepError(t('gest:management.errors.off_grid_reason'));
      return;
    }
    setStepError(null);
    // P-ADM-16 : payload plein hydrate depuis `record` + etat formulaire.
    // Les raisons / note sont passees explicitement (etat UI) ; le helper
    // garantit aussi le report si un appelant omettait ces champs.
    setComp.mutate(
      {
        email,
        payload: buildCompensationPayload(record, {
          classId,
          // Hors grille : echelon non applicable -> null (le serveur conserve l'existant).
          currentEchelon: offGrid ? null : Number(echelon),
          currentNiveau: niveau === '' ? null : Number(niveau),
          hireDate: hireDate || null,
          offGrid,
          // M-752 : decocher efface (le serveur aussi, on reflete).
          offGridReasonPrivate: offGrid ? (reasonPrivate.trim() || null) : null,
          offGridReasonPublic: offGrid ? (reasonPublic.trim() || null) : null,
          roleFitFlag: roleFit,
          roleFitNote: roleFit ? (roleFitNote.trim() || null) : null,
        }),
      },
      { onError: (err) => onError(managementErrorCode(err)) },
    );
  }

  const labelCls = 'text-meta font-medium text-slate-600 dark:text-slate-300';

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="class-id" className={labelCls}>{t('gest:management.class.class')}</label>
          <select
            id="class-id"
            value={classId}
            onChange={(e) => {
              // M-754 : changement de classe -> reset niveau.
              setClassId(e.target.value);
              setNiveau('');
            }}
            className={`${GS_COMPONENTS.input} w-56`}
          >
            <option value="">{t('gest:management.class.chooseClass')}</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="class-niveau" className={labelCls}>{t('gest:management.class.niveau')}</label>
          <select id="class-niveau" value={niveau} onChange={(e) => setNiveau(e.target.value)} className={`${GS_COMPONENTS.input} w-64`}>
            <option value="">{t('gest:management.class.niveauUndefined')}</option>
            {(cls?.niveaux ?? []).map((n) => (
              <option key={n.level} value={String(n.level)}>
                {t('gest:management.class.niveauOption', { level: n.level, label: n.label })}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="class-echelon" className={labelCls}>{t('gest:management.class.echelon')}</label>
          <input
            id="class-echelon"
            type="number"
            min={1}
            max={maxEchelon}
            value={echelon}
            disabled={offGrid}
            onChange={(e) => setEchelon(e.target.value)}
            className={`${GS_COMPONENTS.input} w-24 disabled:opacity-50`}
          />
          <span className="text-chip text-slate-500 dark:text-slate-400">
            {offGrid
              ? t('gest:management.class.echelonOffGrid')
              : selectedNiveau?.echelonMax != null
                ? t('gest:management.class.echelonMax', { max: selectedNiveau.echelonMax })
                : ''}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="class-hire" className={labelCls}>{t('gest:management.class.hireDate')}</label>
          <input id="class-hire" type="date" value={hireDate} onChange={(e) => setHireDate(e.target.value)} className={`${GS_COMPONENTS.input} w-44`} />
        </div>
      </div>

      {/* Notion A — Hors grille */}
      <div className="flex flex-col gap-3 border-t border-slate-200 pt-3 dark:border-slate-700">
        <label htmlFor="class-off-grid" className="flex items-center gap-2 text-field text-slate-700 dark:text-slate-200">
          <input
            id="class-off-grid"
            type="checkbox"
            checked={offGrid}
            onChange={(e) => {
              // M-752 : decocher -> efface les raisons cote UI.
              setOffGrid(e.target.checked);
              if (!e.target.checked) {
                setReasonPrivate('');
                setReasonPublic('');
              }
            }}
            className="size-4 rounded border-slate-300 text-brand-600 dark:border-slate-600"
          />
          {t('gest:management.class.offGridLabel')}
        </label>
        {offGrid ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="off-grid-private" className={labelCls}>{t('gest:management.class.reasonPrivate')}</label>
              <textarea id="off-grid-private" required maxLength={2000} value={reasonPrivate} onChange={(e) => setReasonPrivate(e.target.value)} placeholder={t('gest:management.class.reasonPrivatePlaceholder')} className={GS_COMPONENTS.input} rows={3} />
              <p className="text-chip text-slate-500 dark:text-slate-400">{t('gest:management.class.reasonPrivateHint')}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="off-grid-public" className={labelCls}>{t('gest:management.class.reasonPublic')}</label>
              <textarea id="off-grid-public" maxLength={2000} value={reasonPublic} onChange={(e) => setReasonPublic(e.target.value)} placeholder={t('gest:management.class.reasonPublicPlaceholder')} className={GS_COMPONENTS.input} rows={3} />
              <p className="text-chip text-slate-500 dark:text-slate-400">{t('gest:management.class.reasonPublicHint')}</p>
            </div>
          </div>
        ) : null}

        {/* Notion B — Adequation au role */}
        <label htmlFor="class-role-fit" className="flex items-center gap-2 text-field text-slate-700 dark:text-slate-200">
          <input
            id="class-role-fit"
            type="checkbox"
            checked={roleFit}
            onChange={(e) => {
              setRoleFit(e.target.checked);
              if (!e.target.checked) setRoleFitNote('');
            }}
            className="size-4 rounded border-slate-300 text-brand-600 dark:border-slate-600"
          />
          {t('gest:management.class.roleFitLabel')}
        </label>
        {roleFit ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="role-fit-note" className={labelCls}>{t('gest:management.class.roleFitNote')}</label>
            <textarea id="role-fit-note" maxLength={2000} value={roleFitNote} onChange={(e) => setRoleFitNote(e.target.value)} placeholder={t('gest:management.class.roleFitNotePlaceholder')} className={GS_COMPONENTS.input} rows={2} />
          </div>
        ) : null}
      </div>

      {stepError ? <p role="alert" className="text-field text-red-600 dark:text-red-400">{stepError}</p> : null}

      <div className="flex justify-end">
        <button type="submit" disabled={setComp.isPending} className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}>
          <Save size={16} aria-hidden="true" />
          {t('gest:management.class.save')}
        </button>
      </div>
    </form>
  );
}
